import { useEffect, useMemo, useState } from "react"
import { Card, CardContent } from "./ui/card"
import { Moon, AudioLines, Megaphone, Volume2, Mic } from "lucide-react"
import { DataService } from "../services/data.service"
import { SleepSoundNight, SleepSoundNightDetail } from "../models/sleepSound.model"
import { getCurrentDate } from "../lib/utils"
import { DateTime } from "luxon"
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, XAxis, YAxis } from "recharts"
import { ChartConfig, ChartContainer, ChartTooltip } from "./ui/chart"

export interface SleepSoundReportProps {
    beneficiaryUserId: number | null   // user.person_user_id
    timezone: string                   // user.time_zone
    // Bumped by the page's 15-min silent refresh (see WatchHealthReport).
    refreshKey?: number
}

type Range = 7 | 30 | 90

// Fallback when the selected range is empty, so a phone that stopped
// listening weeks ago still shows its history (and the toggle). The backend
// clamps `days` to 366.
const ALL_HISTORY_DAYS = 3650

// Timeline resolution, minutes.
const BUCKET_MIN = 5

// Intensity bands, dB above room noise (not absolute dB — see the model).
const BAND = {
    light: { label: "light", color: "#c7d2fe" },
    moderate: { label: "moderate", color: "#818cf8" },
    loud: { label: "loud", color: "#4338ca" },
}
const NOT_LISTENING_COLOR = "#e5e7eb"
const INTENSITY_COLOR = "#f59e0b"

function band(db: number | null) {
    if (db == null) return null
    if (db < 10) return BAND.light
    if (db < 20) return BAND.moderate
    return BAND.loud
}

const trendConfig: ChartConfig = {
    quiet_min: { label: "Snoring", color: BAND.moderate.color },
    loud_min: { label: "Loud snoring", color: BAND.loud.color },
    intensity_db: { label: "dB above room", color: INTENSITY_COLOR },
}
const timelineConfig: ChartConfig = {
    snore_s: { label: "Snoring", color: BAND.moderate.color },
    gap: { label: "Not listening", color: NOT_LISTENING_COLOR },
}

function rangeButtonCls(active: boolean) {
    return active
        ? 'px-3 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg'
        : 'px-3 py-2 text-sm font-medium bg-gray-200 rounded-lg hover:bg-gray-300'
}

// 63.5 → "1 h 4 min", 9 → "9 min".
function fmtMinutes(m: number) {
    const total = Math.round(m)
    if (total < 60) return `${total} min`
    const h = Math.floor(total / 60)
    const rest = total % 60
    return rest ? `${h} h ${rest} min` : `${h} h`
}

function fmtIntensity(db: number | null) {
    const b = band(db)
    return db == null || !b ? "No snoring" : `${Math.round(db)} dB above room · ${b.label}`
}

// Night dates are calendar dates (the morning the night ends) — parse them
// without a zone shift so "2026-10-19" always reads Oct 19.
function nightLabel(date: string, fmt = "MMM d") {
    return DateTime.fromISO(date).toFormat(fmt)
}

function NightTooltip({ active, payload }: any) {
    const n: SleepSoundNight | undefined = active && payload?.[0]?.payload?.night
    if (!n) return null
    return (
        <div className="rounded-lg border bg-white px-3 py-2 text-xs shadow-md space-y-0.5">
            <p className="font-medium text-gray-800">Night ending {nightLabel(n.date, "EEE, MMM d")}</p>
            <p>Snoring: {fmtMinutes(n.snore_min)} ({n.snore_pct}% of the night)</p>
            <p>Loud snoring: {fmtMinutes(n.loud_snore_min)}</p>
            <p>Intensity: {fmtIntensity(n.intensity_db)}</p>
            <p>Listened: {fmtMinutes(n.listened_min)}</p>
        </div>
    )
}

function BucketTooltip({ active, payload }: any) {
    const b = active && payload?.[0]?.payload
    if (!b) return null
    return (
        <div className="rounded-lg border bg-white px-3 py-2 text-xs shadow-md space-y-0.5">
            <p className="font-medium text-gray-800">{b.label}</p>
            {b.listened_s === 0
                ? <p className="text-gray-500">Not listening</p>
                : <>
                    <p>Snoring: {b.snore_s} s of {b.listened_s} s</p>
                    {b.snore_s > 0 && <p>Intensity: {fmtIntensity(b.intensity_db)}</p>}
                    {b.loud_events > 0 && <p>Other loud sounds: {b.loud_events}</p>}
                </>}
        </div>
    )
}

export function SleepSoundReport(props: SleepSoundReportProps) {
    const [nights, setNights] = useState<SleepSoundNight[] | null>(null)
    const [days, setDays] = useState<Range>(30)
    const [fellBack, setFellBack] = useState(false)
    // Night whose tiles + timeline are shown; null = newest.
    const [selected, setSelected] = useState<string | null>(null)
    const [detail, setDetail] = useState<SleepSoundNightDetail | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)

    useEffect(() => {
        let cancelled = false
        const uid = props.beneficiaryUserId
        if (uid == null) { setNights([]); setFellBack(false); return }
        // Keep the old data on screen while a new range loads (no flicker).
        const load = async () => {
            let rows = await DataService.getSleepSoundNights(uid, days)
            let widened = false
            if (rows.length === 0) {
                rows = await DataService.getSleepSoundNights(uid, ALL_HISTORY_DAYS)
                widened = rows.length > 0
            }
            if (cancelled) return
            setNights(rows)
            setFellBack(widened)
        }
        load().catch(() => { if (!cancelled) { setNights([]); setFellBack(false) } })
        return () => { cancelled = true }
    }, [props.beneficiaryUserId, days, props.refreshKey])

    // Newest first; don't trust server order.
    const sorted = useMemo(() => [...(nights || [])].sort((a, b) => (a.date < b.date ? 1 : -1)), [nights])
    const night = sorted.find(n => n.date === selected) ?? sorted[0] ?? null
    const nightDate = night?.date ?? null

    useEffect(() => {
        let cancelled = false
        const uid = props.beneficiaryUserId
        if (uid == null || nightDate == null) { setDetail(null); return }
        setDetailLoading(true)
        DataService.getSleepSoundNight(uid, nightDate, BUCKET_MIN)
            .then(r => { if (!cancelled) setDetail(r) })
            .finally(() => { if (!cancelled) setDetailLoading(false) })
        return () => { cancelled = true }
    }, [props.beneficiaryUserId, nightDate, props.refreshKey])

    // Ascending, one bar per night with data. Loud snoring is stacked on top
    // of the rest so the bar's total height is snore_min.
    const trendData = useMemo(() => [...sorted].reverse().map(n => ({
        date: n.date,
        label: nightLabel(n.date),
        quiet_min: Math.max(0, n.snore_min - n.loud_snore_min),
        loud_min: n.loud_snore_min,
        intensity_db: n.intensity_db,
        night: n,
    })), [sorted])

    // Continuous bucket grid from the first to the last bucket. A bucket the
    // server omitted had no rows at all, i.e. the phone wasn't listening.
    const timelineData = useMemo(() => {
        if (!detail || detail.date !== nightDate || !detail.buckets.length) return []
        const stepMs = (detail.bucket_min || BUCKET_MIN) * 60_000
        const maxS = stepMs / 1000
        const byT = new Map(detail.buckets.map(b => [b.t, b]))
        const first = Math.min(...detail.buckets.map(b => b.t))
        const last = Math.max(...detail.buckets.map(b => b.t))
        const out = []
        for (let t = first; t <= last; t += stepMs) {
            const b = byT.get(t)
            const listened = b?.listened_s ?? 0
            out.push({
                t,
                label: DateTime.fromMillis(t).setZone(props.timezone).toFormat("HH:mm"),
                listened_s: listened,
                snore_s: listened > 0 ? (b?.snore_s ?? 0) : 0,
                intensity_db: b?.intensity_db ?? null,
                loud_events: b?.loud_events ?? 0,
                gap: listened === 0 ? maxS : 0,
            })
        }
        return out
    }, [detail, nightDate, props.timezone])

    if (nights === null) return null
    // Self-hide: no nights at all.
    if (!night) return null

    const isLastNight = night.date === getCurrentDate(0, props.timezone)
    const zoned = (ms: number) => DateTime.fromMillis(ms).setZone(props.timezone).toFormat("HH:mm")
    const nightBand = band(night.intensity_db)
    const bucketMaxS = (detail?.bucket_min || BUCKET_MIN) * 60

    return (
        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="sleep_sound_report">
            <div className="flex justify-between items-center border-b pb-2">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <Moon className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Snoring
                </h3>
            </div>

            <div className="flex justify-center gap-2">
                <button className={rangeButtonCls(days === 7)} onClick={() => setDays(7)}>7 nights</button>
                <button className={rangeButtonCls(days === 30)} onClick={() => setDays(30)}>30 nights</button>
                <button className={rangeButtonCls(days === 90)} onClick={() => setDays(90)}>90 nights</button>
            </div>

            {fellBack && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    No snoring data in the last {days} nights — showing the full history instead.
                </p>
            )}

            {/* ── Selected night (default: newest) ───────────────────────── */}
            <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700">
                    {isLastNight ? "Last night" : `Night ending ${nightLabel(night.date, "EEE, MMM d")}`}
                    {!isLastNight && night.date === sorted[0]?.date && (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                            latest · {DateTime.fromISO(night.date, { zone: props.timezone }).toRelativeCalendar()}
                        </span>
                    )}
                </h4>

                <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                        <div className="flex justify-between items-center">
                            <p className="text-sm font-medium text-gray-800">{fmtMinutes(night.snore_min)}</p>
                            <AudioLines className="text-gray-800" />
                        </div>
                        <span className="text-xs text-gray-500">Snoring · {night.snore_pct}% of the night</span>
                    </div>
                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                        <div className="flex justify-between items-center">
                            <p className="text-sm font-medium text-gray-800">{fmtMinutes(night.loud_snore_min)}</p>
                            <Megaphone className="text-gray-800" />
                        </div>
                        <span className="text-xs text-gray-500">Loud snoring</span>
                    </div>
                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                        <div className="flex justify-between items-center">
                            <p className="text-sm font-medium text-gray-800">
                                {night.intensity_db == null ? "—" : `${Math.round(night.intensity_db)} dB above room`}
                            </p>
                            <Volume2 className="text-gray-800" />
                        </div>
                        <span className="text-xs text-gray-500">Intensity{nightBand ? ` · ${nightBand.label}` : " · no snoring"}</span>
                    </div>
                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                        <div className="flex justify-between items-center">
                            <p className="text-sm font-medium text-gray-800">{fmtMinutes(night.listened_min)}</p>
                            <Mic className="text-gray-800" />
                        </div>
                        <span className="text-xs text-gray-500">Listened · {zoned(night.start)} – {zoned(night.end)}</span>
                    </div>
                </div>
            </div>

            {/* ── Nightly trend ──────────────────────────────────────────── */}
            {trendData.length > 1 && (
                <div className="space-y-1">
                    <h4 className="text-sm font-semibold text-gray-700">Snoring per night (min)</h4>
                    <p className="text-xs text-gray-500">Tap a night to see it below.</p>
                    {/* Axis titles as HTML — a recharts axis label clips at phone width. */}
                    <div className="flex justify-between text-[10px]">
                        <span className="text-gray-500">min</span>
                        <span style={{ color: INTENSITY_COLOR }}>dB above room</span>
                    </div>
                    <CardContent className="p-0">
                        <ChartContainer config={trendConfig}>
                            <ComposedChart accessibilityLayer data={trendData} margin={{ top: 4, left: 0, right: 0, bottom: 0 }}
                                onClick={(e: any) => { const d = e?.activePayload?.[0]?.payload?.date; if (d) setSelected(d) }}>
                                <CartesianGrid vertical={false} />
                                <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} angle={-60} />
                                <YAxis yAxisId="min" tickLine={false} axisLine={false} tickMargin={6} width={32} />
                                <YAxis yAxisId="db" orientation="right" tickLine={false} axisLine={false} tickMargin={6} width={40}
                                    domain={[0, (max: number) => Math.max(10, Math.ceil(max / 10) * 10)]} />
                                <ChartTooltip cursor={{ fill: "rgba(0,0,0,0.05)" }} content={<NightTooltip />} />
                                <Bar yAxisId="min" dataKey="quiet_min" stackId="s" fill={BAND.moderate.color} isAnimationActive={false} className="cursor-pointer">
                                    {trendData.map(d => <Cell key={d.date} fillOpacity={d.date === night.date ? 1 : 0.55} />)}
                                </Bar>
                                <Bar yAxisId="min" dataKey="loud_min" stackId="s" fill={BAND.loud.color} radius={[2, 2, 0, 0]} isAnimationActive={false} className="cursor-pointer">
                                    {trendData.map(d => <Cell key={d.date} fillOpacity={d.date === night.date ? 1 : 0.55} />)}
                                </Bar>
                                <Line yAxisId="db" dataKey="intensity_db" type="monotone" stroke={INTENSITY_COLOR} strokeWidth={2}
                                    dot={{ r: 2, fill: INTENSITY_COLOR }} connectNulls={false} isAnimationActive={false} />
                            </ComposedChart>
                        </ChartContainer>
                    </CardContent>
                    <div className="flex flex-wrap justify-center gap-3 text-xs text-gray-600">
                        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: BAND.moderate.color }} />Snoring</span>
                        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: BAND.loud.color }} />Loud snoring</span>
                        <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: INTENSITY_COLOR }} />Intensity (dB above room)</span>
                    </div>
                </div>
            )}

            {/* ── Night timeline ─────────────────────────────────────────── */}
            <div className="space-y-1">
                <h4 className="text-sm font-semibold text-gray-700">
                    Through the night <span className="text-xs font-normal text-gray-500">· {nightLabel(night.date, "MMM d")} · seconds of snoring per {detail?.bucket_min || BUCKET_MIN} min</span>
                </h4>
                {timelineData.length > 0 ? (
                    <>
                        <CardContent className="p-0">
                            <ChartContainer config={timelineConfig}>
                                <BarChart accessibilityLayer data={timelineData} barCategoryGap={0} margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
                                    <CartesianGrid vertical={false} />
                                    <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
                                    <YAxis tickLine={false} axisLine={false} tickMargin={6} width={32} domain={[0, bucketMaxS]} />
                                    <ChartTooltip cursor={{ fill: "rgba(0,0,0,0.05)" }} content={<BucketTooltip />} />
                                    <Bar dataKey="snore_s" stackId="t" isAnimationActive={false}>
                                        {timelineData.map(b => <Cell key={b.t} fill={(band(b.intensity_db) ?? BAND.light).color} />)}
                                    </Bar>
                                    <Bar dataKey="gap" stackId="t" fill={NOT_LISTENING_COLOR} isAnimationActive={false} />
                                </BarChart>
                            </ChartContainer>
                        </CardContent>
                        <div className="flex flex-wrap justify-center gap-3 text-xs text-gray-600">
                            {Object.values(BAND).map(b => (
                                <span key={b.label} className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: b.color }} />{b.label}</span>
                            ))}
                            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: NOT_LISTENING_COLOR }} />not listening</span>
                        </div>
                    </>
                ) : (
                    <p className="text-xs text-gray-500">{detailLoading ? "Loading…" : "No timeline for this night."}</p>
                )}
            </div>

            <p className="text-xs text-gray-500 border-t pt-2">
                Measured with the phone microphone. Audio is analysed on the phone and never recorded. Not a medical test.
            </p>
        </Card>
    )
}
